import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '@/lib/api';
import type { BillingPlan } from './use-admin-billing';

export interface MySubscription {
  subscription_id: string;
  billing_plan_id: string;
  quantity: number;
  extra_ai_agents: number;
  status: string;
  blocked_at: string | null;
  current_period_end: string | null;
}

export interface SubscribeResult {
  subscription: MySubscription;
  client_secret: string | null;
  mode: 'payment' | 'setup';
}

export interface AgenciaWorkspace { workspace_id: string; name: string; owner_user_id: string; created_at: string; }
export interface AgenciaOverviewItem { subscription: MySubscription; plan: BillingPlan; workspaces: AgenciaWorkspace[]; }
export interface AgenciaInvite {
  workspace_provisioning_id: string; subscription_id: string; invitee_email: string;
  workspace_name: string; token: string; status: string; expires_at: string; created_at: string;
}
export interface Invoice { id: string; amount_due: number; currency: string; status: string | null; created: number; hosted_invoice_url: string | null; }

export function useMySubscriptions() {
  return useQuery({ queryKey: ['subscriptions', 'me'], queryFn: () => apiFetch<{ data: MySubscription[] }>('/api/subscriptions/me'), staleTime: 30_000 });
}

export function usePublicBillingPlans() {
  return useQuery({ queryKey: ['billing-plans', 'public'], queryFn: () => apiFetch<{ data: BillingPlan[] }>('/api/billing-plans'), staleTime: 60_000 });
}

export function useSubscribe() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (billing_plan_id: string) =>
      apiFetch<{ data: SubscribeResult }>('/api/subscriptions', { method: 'POST', body: JSON.stringify({ billing_plan_id }) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['subscriptions', 'me'] }),
  });
}

export function useSetExtraAgents() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { subscription_id: string; extra_ai_agents: number }) =>
      apiFetch<{ data: MySubscription }>(`/api/subscriptions/${vars.subscription_id}`, {
        method: 'PATCH',
        body: JSON.stringify({ action: 'set_ai_agents', extra_ai_agents: vars.extra_ai_agents }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['subscriptions', 'me'] });
      qc.invalidateQueries({ queryKey: ['plan-me'] });
    },
  });
}

export function useCancelSubscription() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (subscription_id: string) =>
      apiFetch<{ ok: true }>(`/api/subscriptions/${subscription_id}`, { method: 'PATCH', body: JSON.stringify({ action: 'cancel' }) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['subscriptions', 'me'] }),
  });
}

export function useAgenciaOverview() {
  return useQuery({ queryKey: ['agencia', 'overview'], queryFn: () => apiFetch<{ data: AgenciaOverviewItem[] }>('/api/agencia/workspaces'), staleTime: 15_000 });
}

export function useAgenciaInvites() {
  return useQuery({ queryKey: ['agencia', 'invites'], queryFn: () => apiFetch<{ data: AgenciaInvite[] }>('/api/agencia/invites'), staleTime: 15_000 });
}

export function useCreateAgenciaWorkspace() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { subscription_id: string; workspace_name: string; invitee_email: string }) =>
      apiFetch<{ data: { provisioning: AgenciaInvite; url: string } }>('/api/agencia/workspaces', { method: 'POST', body: JSON.stringify(vars) }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['agencia', 'overview'] }); qc.invalidateQueries({ queryKey: ['agencia', 'invites'] }); },
  });
}

export function useRevokeInvite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiFetch<{ ok: true }>(`/api/agencia/invites/${id}/revoke`, { method: 'POST' }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['agencia', 'invites'] }); qc.invalidateQueries({ queryKey: ['agencia', 'overview'] }); },
  });
}

export function useInvoices() {
  return useQuery({ queryKey: ['billing', 'invoices'], queryFn: () => apiFetch<{ data: Invoice[] }>('/api/billing/invoices'), staleTime: 60_000 });
}

/** Deriva se o user é "agência" (tem subscription reseller ativa). */
export function useIsAgency() {
  const subs = useMySubscriptions();
  const plans = usePublicBillingPlans();
  const resellerPlanIds = new Set((plans.data?.data ?? []).filter((p) => p.plan_type === 'reseller').map((p) => p.billing_plan_id));
  const isAgency = (subs.data?.data ?? []).some(
    (s) => s.status === 'active' && s.blocked_at == null && resellerPlanIds.has(s.billing_plan_id),
  );
  return { isAgency, isLoading: subs.isLoading || plans.isLoading };
}
